import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { handleMoodleCallback } from '../api/moodleAuthApi';
import { useAuthMeQuery } from '../api/authApi';

/**
 * Moodle OAuth Callback Page
 * Handles the redirect from backend after Moodle authentication
 * Extracts token from URL, stores it, fetches user profile, and redirects to dashboard
 */
const MoodleCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(true);

  // Get auth/me query (disabled initially)
  const { refetch: fetchUserProfile } = useAuthMeQuery({
    enabled: false
  });

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Get token from URL query parameters
        const token = searchParams.get('token');

        if (!token) {
          setError('Authentication failed. No token received.');
          setIsProcessing(false);
          setTimeout(() => {
            navigate('/login?error=missing_token');
          }, 2000);
          return;
        }

        // Store token in localStorage
        handleMoodleCallback(token);

        // Fetch user profile to populate auth context
        const result = await fetchUserProfile();

        if (result.isError) {
          setError('Failed to load user profile. Please try again.');
          setIsProcessing(false);
          setTimeout(() => {
            navigate('/login?error=profile_fetch_failed');
          }, 2000);
          return;
        }

        // Success - redirect to dashboard
        setIsProcessing(false);
        navigate('/dashboard', { replace: true });
      } catch (err) {
        console.error('Moodle callback error:', err);
        setError('An unexpected error occurred. Please try again.');
        setIsProcessing(false);
        setTimeout(() => {
          navigate('/login?error=callback_error');
        }, 2000);
      }
    };

    processCallback();
  }, [searchParams, navigate, fetchUserProfile]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8 p-8">
        <div className="text-center">
          {isProcessing ? (
            <>
              <div className="flex justify-center mb-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Completing Sign In...
              </h2>
              <p className="text-gray-600">
                Please wait while we set up your account.
              </p>
            </>
          ) : error ? (
            <>
              <div className="flex justify-center mb-4">
                <svg
                  className="h-12 w-12 text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Authentication Failed
              </h2>
              <p className="text-gray-600">{error}</p>
              <p className="text-sm text-gray-500 mt-2">
                Redirecting to login page...
              </p>
            </>
          ) : (
            <>
              <div className="flex justify-center mb-4">
                <svg
                  className="h-12 w-12 text-green-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Sign In Successful
              </h2>
              <p className="text-gray-600">
                Redirecting to dashboard...
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MoodleCallbackPage;
